import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Avatar } from "@/components/Avatar";
import { ARCHETYPES } from "@/lib/archetypes";
import { getPublicProfile } from "@/lib/profile.functions";
import { Award, MapPin, Briefcase, Sparkles, Flame, Brain, Trophy, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/u/$username")({
  loader: async ({ params }) => {
    const res = await getPublicProfile({ data: { username: params.username } });
    if (!res) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    const p = loaderData?.profile;
    if (!p) return { meta: [{ title: "Profile · Smartasaurus" }] };
    const name = p.display_name || p.username || "Smartasaurus";
    return {
      meta: [
        { title: `${name} · Smartasaurus` },
        { name: "description", content: p.bio ?? `${name} on Smartasaurus.` },
        { property: "og:title", content: `${name} · Smartasaurus` },
        { property: "og:description", content: p.bio ?? `Meet ${name} on Smartasaurus.` },
      ],
    };
  },
  errorComponent: ({ error }: ErrorComponentProps) => (
    <div className="min-h-screen bg-background"><SiteHeader /><main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl uppercase">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
    </main><SiteFooter /></div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-background"><SiteHeader /><main className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl uppercase">Profile not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">This user doesn't exist or has a private profile.</p>
      <Link to="/" className="mt-6 inline-block rounded-full bg-primary px-5 py-2 text-xs font-bold uppercase tracking-widest text-primary-foreground">Back home</Link>
    </main><SiteFooter /></div>
  ),
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const data = Route.useLoaderData() as { profile: any; badges: { slug: string; name: string; icon: string | null; earned_at: string }[]; stats: { score: number; streak_days: number; discoveries_viewed: number; quizzes_completed: number } };
  const { profile, badges, stats } = data;
  const arch = profile.archetype ? ARCHETYPES[profile.archetype as keyof typeof ARCHETYPES] : null;
  const name = profile.display_name || profile.username || "Smartasaurus";
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-16 md:px-8 md:py-20">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
          <div className="flex flex-col items-start gap-6 md:flex-row">
            <Avatar seed={profile.avatar_seed ?? profile.username} style={profile.avatar_style} size={144} className="ring-2 ring-primary/40" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">Smartasaurus</div>
              <h1 className="mt-1 font-display text-4xl uppercase leading-tight md:text-5xl">{name}</h1>
              {profile.username && <p className="mt-1 text-sm text-muted-foreground">@{profile.username}</p>}
              {profile.bio && <p className="mt-4 text-base">{profile.bio}</p>}

              <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                {profile.occupation && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {profile.occupation}</span>}
                {profile.country && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {profile.country}</span>}
              </div>

              {arch && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs text-primary">
                  <span className="text-base">{arch.emoji}</span>
                  <span className="font-bold uppercase tracking-widest">{arch.name}</span>
                  <span className="italic text-primary/80">· {arch.tagline}</span>
                </div>
              )}

              {profile.interests && profile.interests.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Curious about</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(profile.interests as string[]).map((i: string) => (
                      <span key={i} className="rounded-full border border-border bg-background px-3 py-1 text-xs">{i}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
          <PubStat icon={<Sparkles className="h-4 w-4" />} label="Curiosity" value={stats.score} />
          <PubStat icon={<Flame className="h-4 w-4" />} label="Streak" value={`${stats.streak_days}d`} />
          <PubStat icon={<Brain className="h-4 w-4" />} label="Discoveries" value={stats.discoveries_viewed} />
          <PubStat icon={<Trophy className="h-4 w-4" />} label="Quizzes" value={stats.quizzes_completed} />
        </section>

        {badges.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              <Award className="h-3.5 w-3.5" /> Badges
            </div>
            <h2 className="mt-2 font-display text-2xl uppercase">Achievements</h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {badges.map((b) => (
                <div key={b.slug} className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
                  <Award className="h-5 w-5 text-primary" />
                  <div className="mt-3 font-display text-sm uppercase">{b.name}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">{new Date(b.earned_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {arch && (
          <section className="mt-10 rounded-3xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Recommended
            </div>
            <h2 className="mt-2 font-display text-2xl uppercase">Topics for {arch.name}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {arch.categoryAffinity.map((slug) => (
                <Link key={slug} to="/category/$slug" params={{ slug }} className="rounded-full border border-border bg-background px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-primary hover:text-primary">
                  {slug.replace(/-/g, " ")}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 rounded-3xl border border-primary/30 bg-primary/5 p-6 text-center md:p-10">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Curious yet?</div>
          <h2 className="mt-2 font-display text-2xl uppercase md:text-3xl">Discover something you never knew you wanted to know.</h2>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/discoveries" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110">
              Explore discoveries <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/collections" className="rounded-full border border-border bg-background px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-primary">
              Browse series
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function PubStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">{icon}{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl">{value}</div>
    </div>
  );
}
